import { createAction, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { CoreMsgTypes } from '@tassat/fred-definitions';

export type ValidationData = {
    pending?: boolean;
    timeout?: boolean;
    loading: boolean;
    error: boolean;
    errorMsg: string;
    confirmationId?: string;
    requestId?: string;
};

export interface ValidationState {
    send: ValidationData;
    order: ValidationData;
    withdrawal: ValidationData;
    pledgeCollateral: ValidationData;
}

const initialState: ValidationState = {
    send: {
        timeout: false,
        pending: true,
        loading: false,
        error: false,
        errorMsg: '',
        confirmationId: undefined,
        requestId: undefined
    },
    order: {
        timeout: false,
        loading: false,
        pending: true,
        error: false,
        errorMsg: '',
        confirmationId: undefined,
        requestId: undefined
    },
    pledgeCollateral: {
        timeout: false,
        pending: true,
        loading: false,
        error: false,
        errorMsg: '',
        confirmationId: undefined,
        requestId: undefined
    },
    withdrawal: {
        timeout: false,
        loading: false,
        pending: true,
        error: false,
        errorMsg: '',
        confirmationId: undefined,
        requestId: undefined
    }
};

// Byte values from core
const SUCCESS_STATUS = 48;
const ERROR_STATUS = 49;

type StatusPayload = {
    Status?: number;
    ConfirmationId?: string;
    RequestId?: string;
};

function setSuccess(field: ValidationData, payload: StatusPayload) {
    field.pending = false;
    field.loading = false;
    field.error = false;
    field.errorMsg = '';
    field.requestId = payload.RequestId;
    field.confirmationId = payload.ConfirmationId;
}

function setError(field: ValidationData) {
    field.pending = false;
    field.loading = false;
    field.error = true;
    field.errorMsg = '';
    field.requestId = undefined;
    field.confirmationId = undefined;
}

function setTimeoutError(field: ValidationData) {
    field.timeout = true;
    field.pending = false;
    field.loading = false;
    field.error = true;
    field.errorMsg = '';
    field.requestId = undefined;
    field.confirmationId = undefined;
}

const resetValidationAction = createAction('RESET_VALIDATION');
const sendTimeoutAction = createAction('SEND_TIMEOUT');
const orderTimeoutAction = createAction('ORDER_TIMEOUT');
const withdrawalTimeoutAction = createAction('WITHDRAWAL_TIMEOUT');
const pledgeCollateralTimeoutAction = createAction('PLEDGE_COLLATERAL_TIMEOUT');

const customerSendApprovalReqAction = createAction<StatusPayload>(CoreMsgTypes.CustomerSendApprovalReq);
const customerSendReqAction = createAction<StatusPayload>(CoreMsgTypes.CustomerSendReq);
const customerPendingSendAckAction = createAction<StatusPayload>(CoreMsgTypes.CustomerPendingSendAck);

const customerPledgeAckAction = createAction<StatusPayload>(CoreMsgTypes.CustomerPledgeAck);
const customerPledgeAddOnAckAction = createAction<StatusPayload>(CoreMsgTypes.CustomerPledgeAddOnAck);
const customerPledgeAddOnPendingAckAction = createAction<StatusPayload>(CoreMsgTypes.CustomerPledgeAddOnPendingAck);
const customerPledgePendingAckAction = createAction<StatusPayload>(CoreMsgTypes.CustomerPledgePendingAck);
const customerPledgeReqAction = createAction<StatusPayload>(CoreMsgTypes.CustomerPledgeReq);
const customerPledgeRejAction = createAction<StatusPayload>(CoreMsgTypes.CustomerPledgeRej);
const customerPledgeAddOnRejAction = createAction<StatusPayload>(CoreMsgTypes.CustomerPledgeAddOnRej);
const customerPledgeAddOnReqAction = createAction<StatusPayload>(CoreMsgTypes.CustomerPledgeAddOnReq);

const customerReleaseAckAction = createAction<StatusPayload>(CoreMsgTypes.CustomerReleaseAck);
const customerReleasePendingAckAction = createAction<StatusPayload>(CoreMsgTypes.CustomerReleasePendingAck);
const customerReleaseRejAction = createAction<StatusPayload>(CoreMsgTypes.CustomerReleaseRej);
const customerReleaseReqAction = createAction<StatusPayload>(CoreMsgTypes.CustomerReleaseReq);
const customerReclaimAckAction = createAction<StatusPayload>(CoreMsgTypes.CustomerReclaimAck);
const customerReclaimPendingAckAction = createAction<StatusPayload>(CoreMsgTypes.CustomerReclaimPendingAck);
const customerReclaimRejAction = createAction<StatusPayload>(CoreMsgTypes.CustomerReclaimRej);
const customerReclaimReqAction = createAction<StatusPayload>(CoreMsgTypes.CustomerReclaimReq);

const customerOrderNewReqAction = createAction<StatusPayload>(CoreMsgTypes.CustomerOrderNewReq);
const customerOrderNewAckAction = createAction<StatusPayload>(CoreMsgTypes.CustomerOrderNewAck);
const customerPendingOrderNewAckAction = createAction<StatusPayload>(CoreMsgTypes.CustomerPendingOrderNewAck);
const customerOrderNewRejAction = createAction<StatusPayload>(CoreMsgTypes.CustomerOrderNewRej);
const customerPendingOrderCancelAckAction = createAction<StatusPayload>(CoreMsgTypes.CustomerPendingOrderCancelAck);
const customerOrderCancelAckAction = createAction<StatusPayload>(CoreMsgTypes.CustomerOrderCancelAck);
const customerOrderCancelRejAction = createAction<StatusPayload>(CoreMsgTypes.CustomerOrderCancelRej);

const customerPendingCancelPledgeTransactionAckAction = createAction<StatusPayload>(CoreMsgTypes.CustomerPendingCancelPledgeTransactionAck);

const customerTransferPendingAckAction = createAction<StatusPayload>(CoreMsgTypes.CustomerTransferPendingAck);
const customerTransferRejAction = createAction<StatusPayload>(CoreMsgTypes.CustomerTransferRej);
const customerTransferAckAction = createAction<StatusPayload>(CoreMsgTypes.CustomerTransferAck);
const customerTransferReqAction = createAction<StatusPayload>(CoreMsgTypes.CustomerTransferReq);

const customerWithdrawalReqAction = createAction<StatusPayload>(CoreMsgTypes.CustomerWithdrawalReq);
const customerPendingWithdrawalAckAction = createAction<StatusPayload>(CoreMsgTypes.CustomerPendingWithdrawalAck);
const customerWithdrawalAckAction = createAction<StatusPayload>(CoreMsgTypes.CustomerWithdrawalAck);
const customerWithdrawalRejAction = createAction<StatusPayload>(CoreMsgTypes.CustomerWithdrawalRej);

const customerPendingCancelAckAction = createAction<StatusPayload>(CoreMsgTypes.CustomerPendingCancelAck);
const customerPendingApproveDeclineAckAction = createAction<StatusPayload>(CoreMsgTypes.CustomerPendingApproveDeclineAck);
const customerCancelReqAction = createAction<StatusPayload>(CoreMsgTypes.CustomerCancelReq);
const customerCancelRejAction = createAction<StatusPayload>(CoreMsgTypes.CustomerCancelRej);

const validationSlice = createSlice({
    name: 'validation',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(resetValidationAction, () => initialState)

            /** Order cancel — unique: CancelAck always succeeds, CancelRej always errors */
            .addCase(customerOrderCancelAckAction, (state, action) => {
                if (state.order.timeout) return;
                setSuccess(state.order, action.payload);
            })
            .addCase(customerOrderCancelRejAction, (state, action) => {
                if (state.order.timeout) return;
                state.order.pending = false;
                state.order.loading = false;
                state.order.error = true;
                state.order.errorMsg = '';
                state.order.requestId = action.payload.RequestId || '';
                state.order.confirmationId = undefined;
            })

            /** Pending cancel — updates send, withdrawal, pledgeCollateral */
            .addCase(customerPendingCancelAckAction, (state, action) => {
                if (state.withdrawal.timeout) return;
                setSuccess(state.send, action.payload);
                setSuccess(state.withdrawal, action.payload);
                setSuccess(state.pledgeCollateral, action.payload);
            })

            /** Pending approve/decline — updates all fields unconditionally */
            .addCase(customerPendingApproveDeclineAckAction, (state, action) => {
                setSuccess(state.send, action.payload);
                setSuccess(state.order, action.payload);
                setSuccess(state.withdrawal, action.payload);
                setSuccess(state.pledgeCollateral, action.payload);
            })

            /** Cancel request — status conditional, all fields */
            .addCase(customerCancelReqAction, (state, action) => {
                if (action.payload.Status === SUCCESS_STATUS) {
                    setSuccess(state.send, action.payload);
                    setSuccess(state.order, action.payload);
                    setSuccess(state.withdrawal, action.payload);
                    setSuccess(state.pledgeCollateral, action.payload);
                } else if (action.payload.Status === ERROR_STATUS) {
                    state.send.timeout = false;
                    state.send.pending = false;
                    state.send.loading = false;
                    state.send.error = true;
                    state.send.errorMsg = '';
                    state.send.requestId = action.payload.RequestId;
                    state.send.confirmationId = undefined;

                    state.order.timeout = false;
                    state.order.pending = false;
                    state.order.loading = false;
                    state.order.error = true;
                    state.order.errorMsg = '';
                    state.order.requestId = action.payload.RequestId;
                    state.order.confirmationId = undefined;

                    state.withdrawal.timeout = false;
                    state.withdrawal.pending = false;
                    state.withdrawal.loading = false;
                    state.withdrawal.error = true;
                    state.withdrawal.errorMsg = '';
                    state.withdrawal.requestId = action.payload.RequestId;
                    state.withdrawal.confirmationId = undefined;

                    state.pledgeCollateral.timeout = false;
                    state.pledgeCollateral.pending = false;
                    state.pledgeCollateral.loading = false;
                    state.pledgeCollateral.error = true;
                    state.pledgeCollateral.errorMsg = '';
                    state.pledgeCollateral.requestId = action.payload.RequestId;
                    state.pledgeCollateral.confirmationId = undefined;
                }
            })

            /** Cancel rejection — always errors send, withdrawal, pledgeCollateral */
            .addCase(customerCancelRejAction, (state, action) => {
                state.send.timeout = false;
                state.send.pending = false;
                state.send.loading = false;
                state.send.error = true;
                state.send.errorMsg = '';
                state.send.requestId = action.payload.RequestId || '';
                state.send.confirmationId = undefined;

                state.withdrawal.timeout = false;
                state.withdrawal.pending = false;
                state.withdrawal.loading = false;
                state.withdrawal.error = true;
                state.withdrawal.errorMsg = '';
                state.withdrawal.requestId = action.payload.RequestId || '';
                state.withdrawal.confirmationId = undefined;

                state.pledgeCollateral.timeout = false;
                state.pledgeCollateral.pending = false;
                state.pledgeCollateral.loading = false;
                state.pledgeCollateral.error = true;
                state.pledgeCollateral.errorMsg = '';
                state.pledgeCollateral.requestId = action.payload.RequestId || '';
                state.pledgeCollateral.confirmationId = undefined;
            })

            /** Timeout actions */
            .addCase(sendTimeoutAction, (state) => {
                setTimeoutError(state.send);
            })
            .addCase(orderTimeoutAction, (state) => {
                setTimeoutError(state.order);
            })
            .addCase(withdrawalTimeoutAction, (state) => {
                setTimeoutError(state.withdrawal);
            })
            .addCase(pledgeCollateralTimeoutAction, (state) => {
                setTimeoutError(state.pledgeCollateral);
            })

        // Grouped matchers — mirror the shared case blocks from the original switch reducer

            /** Send + Transfer workflow (both mapped to state.send in the original reducer) */
            .addMatcher(
                isAnyOf(
                    customerSendApprovalReqAction,
                    customerSendReqAction,
                    customerPendingSendAckAction,
                    customerTransferPendingAckAction,
                    customerTransferRejAction,
                    customerTransferAckAction,
                    customerTransferReqAction
                ),
                (state, action) => {
                    if (state.send.timeout) return;
                    if (action.payload.Status === SUCCESS_STATUS) {
                        setSuccess(state.send, action.payload);
                    } else if (action.payload.Status === ERROR_STATUS) {
                        setError(state.send);
                    }
                }
            )

            /** pledgeCollateral workflow */
            .addMatcher(
                isAnyOf(
                    customerPledgeAckAction,
                    customerPledgeAddOnAckAction,
                    customerPledgeAddOnPendingAckAction,
                    customerPledgePendingAckAction,
                    customerPledgeReqAction,
                    customerPledgeRejAction,
                    customerPledgeAddOnRejAction,
                    customerPledgeAddOnReqAction,
                    customerReleaseAckAction,
                    customerReleasePendingAckAction,
                    customerReleaseRejAction,
                    customerReleaseReqAction,
                    customerReclaimAckAction,
                    customerReclaimPendingAckAction,
                    customerReclaimRejAction,
                    customerReclaimReqAction,
                    customerPendingApproveDeclineAckAction,
                    customerPendingCancelPledgeTransactionAckAction
                ),
                (state, action) => {
                    if (state.pledgeCollateral.timeout) return;
                    if (action.payload.Status === SUCCESS_STATUS) {
                        setSuccess(state.pledgeCollateral, action.payload);
                    } else if (action.payload.Status === ERROR_STATUS) {
                        setError(state.pledgeCollateral);
                    }
                }
            )

            /** Order workflow (excluding CancelAck/CancelRej which have unique logic) */
            .addMatcher(
                isAnyOf(
                    customerOrderNewReqAction,
                    customerOrderNewAckAction,
                    customerPendingOrderNewAckAction,
                    customerOrderNewRejAction,
                    customerPendingOrderCancelAckAction
                ),
                (state, action) => {
                    if (state.order.timeout) return;
                    if (action.payload.Status === SUCCESS_STATUS) {
                        setSuccess(state.order, action.payload);
                    } else if (action.payload.Status === ERROR_STATUS) {
                        setError(state.order);
                    }
                }
            )

            /** Withdrawal workflow */
            .addMatcher(
                isAnyOf(
                    customerWithdrawalReqAction,
                    customerPendingWithdrawalAckAction,
                    customerWithdrawalAckAction,
                    customerWithdrawalRejAction
                ),
                (state, action) => {
                    if (state.withdrawal.timeout) return;
                    if (action.payload.Status === SUCCESS_STATUS) {
                        setSuccess(state.withdrawal, action.payload);
                    } else if (action.payload.Status === ERROR_STATUS) {
                        setError(state.withdrawal);
                    }
                }
            );
    }
});

export default validationSlice.reducer;